import type { EmailVerificationOutcome } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { useEffect } from 'react';

import { Alert, Button, Input, Label, Spinner } from '@fluxora/ui';

import { useToast } from './toast/ToastProvider';

import { isNotImplemented, isSessionExpired } from '../api/bff-client';
import {
  normalizeOtpInput,
  OTP_INPUT_MAX_LENGTH,
  type EmailOtpPhase,
} from '../services/email-verification.service';

/**
 * Email proof-of-control on the credit step (E-SF05) — the field that stands
 * between a typo'd address and an enrollment whose entire paper trail (status link,
 * contract copy, FCRA adverse-action notice, welcome pack) is delivered to a
 * stranger.
 *
 * Purely presentational and prop-driven: every derivation — which of the five
 * states we are in, whether the address is mailable, how many seconds remain — is
 * computed by `email-verification.service` and passed in. This file renders JSX.
 *
 * All five async states:
 *   - `idle`          → the "email me a code" affordance (disabled until the address
 *                       parses, so a typo costs a disabled button, not one of the
 *                       customer's three hourly sends).
 *   - `sending`       → Spinner.
 *   - `error`         → an HONEST Alert. A `501` (SMTP credentials still pending) is
 *                       a WARNING that says so, never a red "something went wrong"
 *                       and never a fake success; a `401` points at the one route
 *                       that legitimately creates a session.
 *   - `awaiting-code` → the code Input + verify + resend-with-countdown, plus the
 *                       server's own `incorrect`/`expired`/`locked` answer verbatim.
 *   - `verified`      → the note that editing the address costs a new code (it does —
 *                       the submit gate re-blocks). The confirmation ITSELF is a
 *                       single tick inside the email input, rendered by
 *                       `EmailVerifiedTick` from the page; see that file for why the
 *                       full-width success pill that used to live here is gone.
 *
 * ── ONE BOUNDED INPUT, NOT SIX BOXES ───────────────────────────────────────
 * A segmented six-box OTP entry is a bespoke primitive (focus management, paste
 * splitting, backspace traversal, screen-reader labelling) and `@fluxora/ui` has no
 * such component. A single bounded field, digit-normalised by `normalizeOtpInput`,
 * with `inputMode="numeric"` and `autoComplete="one-time-code"`, gets iOS/Android
 * code autofill for free, is announced correctly, and is one element to tab to.
 *
 * ⚠️ ITS `maxLength` IS DELIBERATELY WIDER THAN SIX. See `OTP_INPUT_MAX_LENGTH`:
 * at six, a customer who COPIED the grouped code out of our own email pasted seven
 * characters, lost the last digit to browser truncation, and was told their code
 * was wrong.
 */
export interface EmailOtpFieldProps {
  readonly phase: EmailOtpPhase;
  /** The code as typed (the page owns it — it is a mutation variable, never cached). */
  readonly code: string;
  readonly onCodeChange: (value: string) => void;
  readonly onSend: () => void;
  readonly onVerify: () => void;
  /** Whole seconds until a resend is allowed; 0 = allowed now. */
  readonly resendInSeconds: number;
  /**
   * True when a code the customer has NOT redeemed is still live (E-SF19 P3).
   *
   * Set on a REFRESH, from the rehydrate snapshot. The phase is still `idle`
   * (the pending address is unproven and never leaves the server, so the SPA
   * cannot match it against the field), but pressing "email me a code" would
   * spend one of only three hourly sends on a code already in the inbox — so
   * the idle affordance says so instead of pretending nothing has happened.
   */
  readonly hasOutstandingCode?: boolean;
  /** False when the address in the email field is not yet mailable. */
  readonly canSend: boolean;
  readonly isVerifying: boolean;
  readonly lastOutcome: EmailVerificationOutcome | null;
  readonly attemptsRemaining: number | null;
  /** i18n key for a client-side code error, or undefined. */
  readonly codeError?: string | undefined;
  readonly sendError?: unknown;
  readonly verifyError?: unknown;
}

/** The i18n key for a non-`verified` server answer. */
const OUTCOME_KEYS: Record<Exclude<EmailVerificationOutcome, 'verified'>, string> = {
  incorrect: 'shop.credit.otp.outcome.incorrect',
  expired: 'shop.credit.otp.outcome.expired',
  locked: 'shop.credit.otp.outcome.locked',
};

export function EmailOtpField({
  phase,
  code,
  onCodeChange,
  onSend,
  onVerify,
  resendInSeconds,
  hasOutstandingCode = false,
  canSend,
  isVerifying,
  lastOutcome,
  attemptsRemaining,
  codeError,
  sendError,
  verifyError,
}: EmailOtpFieldProps): JSX.Element {
  const { t } = useTranslation();
  const { showToast } = useToast();

  /* The failure is a moment, not a state of the form — it surfaces as a toast
     over the page instead of an alert box shoving the fields apart. The retry
     button below is what remains inline. */
  useEffect(() => {
    if (phase !== 'error') return;
    showToast({
      title: t('shop.credit.otp.error.title'),
      body: isNotImplemented(sendError)
        ? t('shop.credit.otp.error.notAvailable')
        : isSessionExpired(sendError)
          ? t('shop.credit.otp.error.sessionExpired')
          : t('shop.credit.otp.error.body'),
      variant: isNotImplemented(sendError) || isSessionExpired(sendError) ? 'warning' : 'danger',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === 'verified') {
    return (
      <div className="flex flex-col gap-1" data-testid="otp-verified">
        {/*
          ⚠️ NO SUCCESS PILL HERE ANY MORE, DELIBERATELY. A full-width green Badge in
          this slot occupied the same shape and position as a FIELD ERROR, so a
          confirmation was drawn in the visual language of a problem. The confirmation
          is now a single tick inside the email input (`EmailVerifiedTick`, mounted by
          CreditPage), which is also where the customer's eye already is.

          The NOTE stays. "If you change it, we'll need to send a new code" is not
          decoration — the submit gate really does re-block on an edit
          (`isVerificationStaleFor`), and a customer who edits without being told finds
          a disabled button and no explanation.
        */}
        <span className="text-[11px] text-foreground/50">{t('shop.credit.otp.verifiedNote')}</span>
      </div>
    );
  }

  if (phase === 'sending') {
    // The button stays where it was, disabled, wearing the spinner — swapping
    // it for a bare spinner row made the layout jump and read as broken.
    return (
      <Button variant="secondary" shape="pill" disabled aria-busy="true">
        <span className="inline-flex items-center gap-2">
          <Spinner size="sm" label={t('shop.credit.otp.sending')} />
          {t('shop.credit.otp.sending')}
        </span>
      </Button>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col gap-2">
        {isSessionExpired(sendError) ? (
          /* ⚠️ NEVER a dead end. When the toast-only redesign first landed,
             this branch rendered NOTHING for an expired session — the toast
             said its piece and vanished, and the customer was left with no
             control under the email field at all. A dead session's one remedy
             is a fresh page (the session re-mints on load), so that is the
             button. */
          <Button variant="secondary" shape="pill" onClick={() => window.location.reload()}>
            {t('shop.credit.otp.reload')}
          </Button>
        ) : (
          // Same floor as every other send affordance. A failed send does not move
          // `resendAvailableAt` (only an ISSUED challenge does), so this is 0 for a
          // first-attempt failure and non-zero only when an earlier send really did
          // go out — which is exactly when a retry would be refused.
          <Button
            variant="secondary"
            shape="pill"
            onClick={onSend}
            disabled={!canSend || resendInSeconds > 0}
          >
            {resendInSeconds > 0
              ? t('shop.credit.otp.sendIn', { seconds: resendInSeconds })
              : t('shop.credit.otp.retry')}
          </Button>
        )}
      </div>
    );
  }

  if (phase === 'awaiting-code') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="cr-otp">{t('shop.credit.otp.code.label')}</Label>
          {/* One muted line, not an info BOX — a sentence in a pill read as
              yet another form control. */}
          <p role="status" className="text-xs text-foreground/60">
            {t('shop.credit.otp.sent')}
          </p>
          <div className="flex items-center gap-2 pt-1">
            <Input
              id="cr-otp"
              name="emailOtp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_INPUT_MAX_LENGTH}
              className="h-11 max-w-[9.5rem] text-center text-lg font-semibold tracking-[0.4em]"
              value={code}
              onChange={(event) => onCodeChange(normalizeOtpInput(event.target.value))}
            />
            <Button
              variant="primary"
              shape="pill"
              className="cta-sheen"
              onClick={onVerify}
              disabled={isVerifying}
            >
              {isVerifying ? t('shop.credit.otp.verifying') : t('shop.credit.otp.verify')}
            </Button>
          </div>
          {codeError !== undefined ? (
            <span className="text-xs text-danger">{t(codeError)}</span>
          ) : null}
        </div>

        {lastOutcome !== null && lastOutcome !== 'verified' ? (
          <span className="text-xs text-danger">
            {t(OUTCOME_KEYS[lastOutcome], { attempts: attemptsRemaining ?? 0 })}
          </span>
        ) : null}

        {verifyError !== undefined && verifyError !== null ? (
          <Alert
            variant={isSessionExpired(verifyError) ? 'warning' : 'danger'}
            title={t('shop.credit.otp.error.verifyTitle')}
            role={isSessionExpired(verifyError) ? 'status' : 'alert'}
          >
            {isSessionExpired(verifyError)
              ? t('shop.credit.otp.error.sessionExpired')
              : t('shop.credit.otp.error.body')}
          </Alert>
        ) : null}

        {/* The countdown is INFORMATION, not an action — quiet text; the
            resend button appears only once resending is actually possible. */}
        {resendInSeconds > 0 ? (
          <span className="text-xs text-foreground/50">
            {t('shop.credit.otp.resendIn', { seconds: resendInSeconds })}
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={onSend}
            disabled={!canSend}
          >
            {t('shop.credit.otp.resend')}
          </Button>
        )}
      </div>
    );
  }

  // idle — nothing requested yet for the address currently in the field.
  //
  // ⚠️ "IDLE" IS NOT "NOTHING HAS HAPPENED". After a refresh the SPA cannot match
  // the pending challenge to the field (the recipient is unproven and never leaves
  // the server), so the phase is `idle` while a live code and a live 60-second
  // resend floor both exist. The button therefore honours `resendInSeconds` here
  // exactly as it does under `awaiting-code`: the ledger the server checks is
  // SESSION-scoped and survives an address change, so a send offered during the
  // floor is one the server would refuse whatever address is in the field.
  const blockedByCooldown = resendInSeconds > 0;
  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="secondary"
        shape="pill"
        onClick={onSend}
        disabled={!canSend || blockedByCooldown}
      >
        {blockedByCooldown
          ? t('shop.credit.otp.sendIn', { seconds: resendInSeconds })
          : t('shop.credit.otp.send')}
      </Button>
      {/*
        E-SF19 P3 — a refresh used to forget an outstanding code entirely, and the
        customer spent one of only three hourly sends getting a second one. We
        cannot say WHICH address it went to (the pending recipient is unproven and
        stays server-side), so the note says what we can honestly say: one is
        already out there, and it is worth checking before asking for another.
      */}
      {hasOutstandingCode ? (
        <span role="status" className="text-[11px] text-foreground/70">
          {t('shop.credit.otp.outstanding')}
        </span>
      ) : null}
    </div>
  );
}
